import http from "../utils/http";
import { getApiBaseUrl } from "@/utils/env";

/**
 * 上传案件材料压缩包（裁判文书首页）
 * POST /api/JudgmentDoc/ImportDossier
 *
 * @param {File|Blob} file        压缩包文件（zip/rar/7z，单文件 ≤ 50MB）
 * @param {string|number} [documentType]  文书类型（GetDocTypeEnumList 枚举的 value）
 * @param {(percent:number)=>void} [onProgress]  上传进度回调
 * @returns {Promise}
 */
export const importDossier = (file, documentType, onProgress) => {
  const formData = new FormData();
  formData.append("file", file);
  if (documentType !== undefined && documentType !== null) {
    formData.append("documentType", documentType);
  }

  // 注意：不要手动设 Content-Type，axios 检测到 FormData 会自动加上
  // 带正确 boundary 的 multipart/form-data 头；手动设反而会让 boundary 丢失，
  // 后端无法解析 multipart body，常导致 500。
  return http.post(`/api/JudgmentDoc/ImportDossier`, formData, {
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const percent = Math.round(
          (progressEvent.loaded / progressEvent.total) * 100,
        );
        onProgress(percent);
      }
    },
  });
};

// 列表查询 接口   /api/JudgmentDoc/GetDocTypeEnumList
export const getGenerationTypeList = () => {
  return http.get("/api/JudgmentDoc/GetDocTypeEnumList");
};

/**
 * 获取文书生成配置（上传压缩包后调用）
 * GET /api/JudgmentDoc/GetGenerationConfig
 *
 * @param {string|number} docId  文书 ID（ImportDossier 接口返回的 docId）
 * @returns {Promise}
 */
export const getGenerationConfig = (docId) => {
  return http.get("/api/JudgmentDoc/GetGenerationConfig", {
    params: { docId },
  });
};

/**
 * 分页查询裁判文书记录
 * GET /api/JudgmentDoc/GetPage
 *
 * @param {Object} params
 * @param {number} params.pageIndex  页码（从 1 开始）
 * @param {number} params.pageSize   每页条数
 * @param {string} [params.keyword]  检索关键字，可缺省
 * @param {string|number} [params.documentType]  文书类型（GetDocTypeEnumList 枚举
 *                                               的 value，调用方默认传判决书对应值）
 * @returns {Promise}
 */
export const getJudgmentPage = ({
  pageIndex,
  pageSize,
  keyword,
  documentType,
}) => {
  return http.get("/api/JudgmentDoc/GetPage", {
    params: {
      PageIndex: pageIndex,
      PageSize: pageSize,
      Keyword: keyword,
      DocumentType: documentType,
    },
  });
};

/**
 * 生成裁判文书（参数放请求体 JSON）
 * POST /api/JudgmentDoc/GenerateJudgment
 *
 * @param {Object} data
 * @param {string} data.docId                 文书 ID（ImportDossier 返回的 docId）
 * @param {string} data.mode                  生成模式
 * @param {string} data.caseReason            案由
 * @param {string} data.applicableProcedure   适用程序
 * @param {string} [data.reasoning]           补充说明（案件情况 / 裁判思路等生成要求），
 *                                            空时不传
 * @param {string} [data.caseType]            案件类型（案件信息表单提交，默认「民事一审」）
 * @param {string} [data.documentSubType]    文书子类型（GetDocTypeEnumList 枚举
 *                                            的 value，字符串形式，如下拉「判决书」为 `"1"`）
 * @returns {Promise}
 */
export const generationJudgment = (data) => {
  return http.post("/api/JudgmentDoc/GenerateJudgment", data);
};

/**
 * AI 问答（基于文书的案件材料，结果页右侧聊天面板）—— SSE 流式接口
 * POST /api/JudgmentDoc/AskFiles
 *
 * 用原生 fetch 消费流：axios/XHR 会等整个响应结束才回调，收不到增量；
 * 鉴权与 contractReview/askQuestion 一致：localStorage token + Bearer 头。
 *
 * @param {Object} params
 * @param {string} params.docId          文书 ID（ImportDossier 返回的 docId）
 * @param {string[]} params.materialIds  案件材料 ID 列表（GetMaterials 返回）
 * @param {string} params.question       用户问题
 * @param {(chunk:string)=>void} [params.onChunk]  每收到一段增量文本回调一次
 * @param {AbortSignal} [params.signal]  取消请求（切换任务 / 组件卸载）
 * @returns {Promise<void>}              流读完 resolve；非 2xx reject
 */
export const askJudgmentFiles = async ({
  docId,
  materialIds,
  question,
  onChunk,
  signal,
}) => {
  // docId / materialIds 是 19 位雪花 ID 字符串：转 JS Number 会丢精度
  // （尾数变 0 → 后端查不到，报「文书不存在」），必须字符串原样直传；
  // 后端 JSON 绑定允许数字字符串（AllowReadingFromString），已实测可行
  const token = localStorage.getItem("token");
  const baseUrl = getApiBaseUrl();
  const res = await fetch(`${baseUrl}api/JudgmentDoc/AskFiles`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ docId, materialIds: materialIds || [], question }),
    signal,
  });

  if (!res.ok) {
    const errText = await res.text().catch(() => "");
    throw new Error(`AskFiles 失败 (${res.status}) ${errText}`);
  }

  // 浏览器不支持流式 body → 退化成一次性读
  if (!res.body) {
    const text = await res.text();
    if (onChunk) onChunk(text);
    return;
  }

  /* ============ SSE 解析（与 contractReview/askQuestion 同套路） ============
   * 事件形如 data:{"content":"…"}（JSON 跨多行展开），事件间空行分隔。
   * chunk 边界与事件边界不对齐 → buffer 暂存按空行切；中文多字节被切开由
   * TextDecoder { stream: true } 内部缓存补全；兼容 \n\n 与 \r\n\r\n 换行。
   * 出错时后端发 data:{"error":"…"} 事件（同样 HTTP 200）。
   *
   * @returns {{content:string}|{error:string}|null}
   */
  const extractEvent = (block) => {
    const trimmed = block.trim();
    if (!trimmed.startsWith("data:")) return null;
    const payload = trimmed.slice(5).trim();
    if (!payload || payload === "[DONE]") return null;
    try {
      const parsed = JSON.parse(payload);
      if (typeof parsed?.error === "string" && parsed.error)
        return { error: parsed.error };
      const hit = parsed?.content ?? parsed?.answer ?? parsed?.text;
      return typeof hit === "string" ? { content: hit } : null;
    } catch (e) {
      // 非 JSON 载荷：按纯文本增量处理
      return { content: payload };
    }
  };

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let raw = ""; // 原始全文：整个响应都不是 SSE 时兜底整体解析
  let received = false;
  let streamError = null;
  let doneReading = false;
  while (!doneReading) {
    const { done, value } = await reader.read();
    if (done) {
      doneReading = true;
      break;
    }
    const text = decoder.decode(value, { stream: true });
    buffer += text;
    raw += text;
    // 按"空行"切出完整事件；最后一段可能不完整，留回 buffer 等下一个 chunk
    const events = buffer.split(/\r?\n\r?\n/);
    buffer = events.pop() || "";
    for (const evt of events) {
      const hit = extractEvent(evt);
      if (!hit) continue;
      if (hit.error) {
        streamError = hit.error;
        break;
      }
      received = true;
      if (onChunk) onChunk(hit.content);
    }
    // 后端明确报错：不再继续读流
    if (streamError) {
      await reader.cancel().catch(() => {});
      throw new Error(streamError);
    }
  }
  // flush decoder 内可能残留的字节
  const tail = decoder.decode();
  if (tail) {
    buffer += tail;
    raw += tail;
  }
  // flush 末尾未跟空行的事件
  if (buffer.trim() && !streamError) {
    const hit = extractEvent(buffer);
    if (hit?.content != null) {
      received = true;
      if (onChunk) onChunk(hit.content);
    } else if (hit?.error) {
      throw new Error(hit.error);
    }
  }
  // 整个响应没有 SSE 事件：兼容非流式整体返回（{code,data:{answer}} 或
  // {code:400,errMsg} 业务错误体 —— 后者直接抛给调用方 toast）
  if (!received && raw.trim()) {
    let parsed = null;
    try {
      parsed = JSON.parse(raw);
    } catch (e) {
      /* 纯文本整体返回 */
    }
    if (parsed) {
      const data = (parsed && (parsed.data || parsed)) || {};
      const answer = data.answer ?? data.content ?? data.text ?? data.message;
      if (typeof answer === "string" && answer) {
        if (onChunk) onChunk(answer);
        return;
      }
      const errMsg = parsed.errMsg ?? parsed.msg ?? data.errMsg;
      if (errMsg) throw new Error(String(errMsg));
    } else if (onChunk) {
      onChunk(raw);
    }
  }
};

/**
 * 更新文书生成状态（暂未接入调用，后续按需使用）
 * POST /api/JudgmentDoc/UpdateGenerateStatus
 *
 * @param {Object} data
 * @param {string} data.docId               文书 ID（ImportDossier 返回的 docId）
 * @param {number} data.generateStatus      生成状态码（0 材料解析中 / 1 资料待补充 /
 *                                          2 生成中 / 3 生成完成 / 4 生成失败）
 * @param {string} data.generateStatusText  状态展示文案
 * @returns {Promise}
 */
export const updateGenerateStatus = ({
  docId,
  generateStatus,
  generateStatusText,
}) => {
  return http.post("/api/JudgmentDoc/UpdateGenerateStatus", {
    docId,
    generateStatus,
    generateStatusText,
  });
};

/**
 * 删除裁判文书记录
 * POST /api/JudgmentDoc/Delete
 *
 * @param {string|number} docId  文书 ID
 * @returns {Promise}
 */
export const deleteJudgmentDoc = (docId) => {
  return http.post("/api/JudgmentDoc/Delete", null, {
    params: { docId },
  });
};

/**
 * 获取文书的全部案件材料
 * GET /api/JudgmentDoc/GetMaterials
 *
 * @param {string|number} documentId  文书 ID（ImportDossier 返回的 documentId）
 * @returns {Promise}                 材料列表
 */
export const getJudgmentMaterials = (documentId) => {
  return http.get("/api/JudgmentDoc/GetMaterials", {
    params: { documentId },
  });
};

/**
 * 补充案件材料（缺材料/补充上传流程）
 * POST /api/JudgmentDoc/AddMaterial
 *
 * @param {string|number} documentId  文书 ID（ImportDossier 返回的 documentId）
 * @param {File|Blob} file            补充材料文件（支持 doc / docx / pdf / 图片）
 * @returns {Promise}
 */
export const addJudgmentMaterial = (documentId, file) => {
  const formData = new FormData();
  formData.append("documentId", documentId);
  formData.append("file", file);
  // 同 importDossier：不手动设 Content-Type，axios 检测到 FormData 会自动带上
  // 正确的 multipart boundary，手动设反而会让后端无法解析
  return http.post("/api/JudgmentDoc/AddMaterial", formData);
};

/**
 * 下载生成的裁判文书
 * GET /api/JudgmentDoc/Download
 *
 * @param {string|number} docId  文书 ID
 * @returns {Promise<Blob>}   文书文件 Blob（docx），可加载进结果页编辑器展示，
 *                            或交给 FileHandler.saveFile 落盘
 */
export const downloadJudgmentDoc = (id) => {
  return http.get("/api/JudgmentDoc/Download", {
    params: { docId: id },
    responseType: "blob",
    // 跳过 http 拦截器的「自动保存到本地」：Blob 返回给调用方，
    // 由调用方决定是展示（结果页）还是落盘（任务行下载）
    skipFileHandler: true,
  });
};
